import { TrendingUp, DollarSign, Users } from 'lucide-react';
import Card, { CardHeader, CardBody } from '../ui/Card';

interface CapitalAllocationDashboardProps {
  data: {
    dividendsPaid: number;
    shareRepurchases: number;
    operatingCashFlow: number;
    period: string;
  };
}

const formatCurrency = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (absValue >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  return `$${value.toFixed(0)}`;
};

export default function CapitalAllocationDashboard({ data }: CapitalAllocationDashboardProps) {
  // Note: dividendsPaid and shareRepurchases are typically negative in cash flow statements
  // We'll use absolute values for display
  const dividends = Math.abs(data.dividendsPaid);
  const buybacks = Math.abs(data.shareRepurchases);
  const totalReturns = dividends + buybacks;
  const payoutRatio = data.operatingCashFlow > 0 ? (totalReturns / data.operatingCashFlow) * 100 : 0;

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-neutral-900">Capital Allocation</h3>
          <span className="text-xs font-medium text-neutral-400 bg-neutral-50 px-2.5 py-1 rounded-full">{data.period}</span>
        </div>
      </CardHeader>
      <CardBody>
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                <div className="text-xs text-blue-700 font-medium">Dividends</div>
              </div>
              <div className="text-xl font-bold text-blue-900">{formatCurrency(dividends)}</div>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <div className="text-xs text-green-700 font-medium">Share Buybacks</div>
              </div>
              <div className="text-xl font-bold text-green-900">{formatCurrency(buybacks)}</div>
            </div>

            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-purple-600" />
                <div className="text-xs text-purple-700 font-medium">Total Returns</div>
              </div>
              <div className="text-xl font-bold text-purple-900">{formatCurrency(totalReturns)}</div>
            </div>
          </div>

          {/* Breakdown */}
          <div className="p-4 bg-neutral-50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-neutral-700">Shareholder Returns Breakdown</span>
              <span className="text-xs text-neutral-600">% of Total</span>
            </div>

            {/* Dividends Bar */}
            {totalReturns > 0 && (
              <div className="mb-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-neutral-700">Dividends</span>
                  <span className="font-semibold text-neutral-900">{formatCurrency(dividends)}</span>
                </div>
                <div className="w-full bg-neutral-200 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full"
                    style={{ width: `${(dividends / totalReturns) * 100}%` }}
                  ></div>
                </div>
                <div className="text-xs text-neutral-600 mt-1">
                  {((dividends / totalReturns) * 100).toFixed(1)}% of total returns
                </div>
              </div>
            )}

            {/* Buybacks Bar */}
            {totalReturns > 0 && (
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-neutral-700">Share Buybacks</span>
                  <span className="font-semibold text-neutral-900">{formatCurrency(buybacks)}</span>
                </div>
                <div className="w-full bg-neutral-200 rounded-full h-3">
                  <div
                    className="bg-green-600 h-3 rounded-full"
                    style={{ width: `${(buybacks / totalReturns) * 100}%` }}
                  ></div>
                </div>
                <div className="text-xs text-neutral-600 mt-1">
                  {((buybacks / totalReturns) * 100).toFixed(1)}% of total returns
                </div>
              </div>
            )}
          </div>

          {/* Payout Ratio */}
          {data.operatingCashFlow > 0 && (
            <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-primary-700 font-medium mb-1">Payout Ratio</div>
                  <div className="text-xs text-primary-600">Total returns as % of operating cash flow</div>
                </div>
                <div className="text-3xl font-bold text-primary-900">{payoutRatio.toFixed(0)}%</div>
              </div>
            </div>
          )}

          {/* Context */}
          <div className="text-xs text-neutral-600 space-y-1">
            <div className="flex justify-between">
              <span>Operating Cash Flow:</span>
              <span className="font-medium text-neutral-900">{formatCurrency(data.operatingCashFlow)}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Shareholder Returns:</span>
              <span className="font-medium text-neutral-900">{formatCurrency(totalReturns)}</span>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
